import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { api } from "../api/axios";
import { PageHeader, Pill } from "../components/ui";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersLimit, setUsersLimit] = useState(25);
  const [usersPage, setUsersPage] = useState(0);
  const [usersStatus, setUsersStatus] = useState("");
  const [usersPlanId, setUsersPlanId] = useState("");
  const [sites, setSites] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("users");
  const [userSearch, setUserSearch] = useState("");
  const [planCode, setPlanCode] = useState("");
  const [planName, setPlanName] = useState("");
  const [planPrice, setPlanPrice] = useState(79);
  const [planInterval, setPlanInterval] = useState("monthly");
  const [planActive, setPlanActive] = useState(1);
  const [planLimitsSites, setPlanLimitsSites] = useState(1);
  const [planDialog, setPlanDialog] = useState({ open: false, user: null, planId: "", months: 1 });
  const [audit, setAudit] = useState([]);
  const [auditError, setAuditError] = useState("");
  const [auditSearch, setAuditSearch] = useState("");
  const [featuresDialog, setFeaturesDialog] = useState({ open: false, plan: null });
  const [featuresDraft, setFeaturesDraft] = useState({});
  const [featuresJson, setFeaturesJson] = useState("");

  const [notificationStats, setNotificationStats] = useState(null);

  const [userDialog, setUserDialog] = useState({ open: false, email: "", name: "", password: "", isAdmin: false });

  const [supportInquiries, setSupportInquiries] = useState([]);
  const [supportTotal, setSupportTotal] = useState(0);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [replyDialog, setReplyDialog] = useState({ open: false, inquiry: null, message: "", subject: "" });
  const [cannedDialog, setCannedDialog] = useState({ open: false, response: null, title: "", content: "" });

  const loadBase = useCallback(async () => {
    const [s, t, sub] = await Promise.all([
      api.get("/api/admin/sites"),
      api.get("/api/admin/transactions"),
      api.get("/api/admin/subscriptions"),
    ]);
    setSites(s.data.sites || []);
    setTransactions(t.data.transactions || []);
    setSubscriptions(sub.data.subscriptions || []);

    const p = await api.get("/api/admin/plans");
    setPlans(p.data.plans || []);

    const a = await api.get("/api/admin/addons");
    setAddons(a.data.addons || []);
  }, []);

  const loadUsers = useCallback(async ({ keepPage = true } = {}) => {
    const page = keepPage ? usersPage : 0;
    const offset = page * usersLimit;
    const { data } = await api.get("/api/admin/users", {
      params: {
        limit: usersLimit,
        offset,
        q: userSearch || undefined,
        status: usersStatus || undefined,
        planId: usersPlanId || undefined,
      },
    });
    setUsers(data.users || []);
    setUsersTotal(Number(data.total || 0));
    if (!keepPage) setUsersPage(0);
  }, [usersLimit, usersPage, userSearch, usersPlanId, usersStatus]);

  useEffect(() => {
    Promise.all([loadBase(), loadUsers({ keepPage: false })]).catch((err) =>
      setError(err?.response?.data?.message || "שגיאה")
    );
  }, [loadBase, loadUsers]);

  // Reload users on filters/pagination change
  useEffect(() => {
    if (tab !== "users") return;
    const t = setTimeout(() => {
      loadUsers().catch((err) => setError(err?.response?.data?.message || "שגיאה"));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, userSearch, usersStatus, usersPlanId, usersPage, usersLimit, loadUsers]);

  useEffect(() => {
    if (tab !== "audit") return;
    api
      .get("/api/admin/audit?limit=300")
      .then(({ data }) => {
        setAudit(data.logs || []);
        setAuditError("");
      })
      .catch((err) => setAuditError(err?.response?.data?.message || "שגיאה"));
  }, [tab]);

  const loadSupport = useCallback(async () => {
    const [inq, can] = await Promise.all([
      api.get("/api/admin/support/inquiries"),
      api.get("/api/admin/support/canned-responses"),
    ]);
    setSupportInquiries(inq.data.inquiries || []);
    setSupportTotal(inq.data.total || 0);
    setCannedResponses(can.data.responses || []);
  }, []);

  useEffect(() => {
    if (tab === "support") {
      loadSupport().catch((err) => setError(err?.response?.data?.message || "שגיאה בטעינת תמיכה"));
    }
  }, [tab, loadSupport]);

  const updateInquiry = async (id, patch) => {
    await api.put(`/api/admin/support/inquiries/${id}`, patch);
    await loadSupport();
  };

  const sendReply = async () => {
    if (!replyDialog.inquiry || !replyDialog.message) return;
    await api.post(`/api/admin/support/inquiries/${replyDialog.inquiry.id}/reply`, {
      message: replyDialog.message,
      subject: replyDialog.subject,
    });
    setReplyDialog({ open: false, inquiry: null, message: "", subject: "" });
    await loadSupport();
  };

  const saveCanned = async () => {
    const id = cannedDialog.response?.id || "new";
    await api.post(`/api/admin/support/canned-responses/${id}`, {
      title: cannedDialog.title,
      content: cannedDialog.content,
    });
    setCannedDialog({ open: false, response: null, title: "", content: "" });
    await loadSupport();
  };

  const deleteCanned = async (id) => {
    if (!window.confirm("למחוק תבנית?")) return;
    await api.delete(`/api/admin/support/canned-responses/${id}`);
    await loadSupport();
  };

  const applyCannedResponse = (res) => {
    if (!replyDialog.inquiry) return;
    let text = res.content;
    text = text.replace(/\[NAME\]/g, replyDialog.inquiry.name || "אורח");
    setReplyDialog((prev) => ({ ...prev, message: text }));
  };

  const knownFeatureKeys = useMemo(
    () => [
      "font",
      "contrast",
      "invertColors",
      "grayscale",
      "links",
      "stopAnimations",
      "readableFont",
      "readLine",
      "readingMask",
      "lineHeight",
      "letterSpacing",
      "paragraphSpacing",
      "focusHighlight",
      "bigCursor",
      "highlightHeadings",
      "highlightForms",
      "highlightImages",
      "cleanReading",
      "textAlignMode",
      "statement",
      "branding",
      "whiteLabel",
      "aiAudit",
      "aiAltText",
      "aiColorSuggestions",
    ],
    []
  );

  const normalizeFeatures = (f) => {
    if (!f) return {};
    if (typeof f === "object") return f;
    try {
      const j = JSON.parse(String(f));
      return typeof j === "object" && j ? j : {};
    } catch {
      return {};
    }
  };

  const formatShekelsFromAgorot = (value) => {
    const num = Number(value || 0);
    if (!Number.isFinite(num)) return "0.00";
    return (num / 100).toFixed(2);
  };

  const setSiteStatus = async (id, status) => {
    await api.put(`/api/admin/sites/${id}/status`, { status });
    await loadBase();
  };

  const setSubStatus = async (id, status) => {
    await api.put(`/api/admin/subscriptions/${id}/status`, { status });
    await loadBase();
  };

  const setUserStatus = async (id, status) => {
    await api.put(`/api/admin/users/${id}/status`, { status });
    await loadUsers();
  };

  const toggleUserAdmin = async (id, currentIsAdmin) => {
    await api.put(`/api/admin/users/${id}/admin`, { isAdmin: !currentIsAdmin });
    await loadUsers();
  };

  const deleteUser = async (id) => {
    if (!window.confirm("למחוק משתמש? פעולה זו תמחק גם אתרים/מנויים/טרנזקציות של המשתמש (Cascade).")) return;
    await api.delete(`/api/admin/users/${id}`);
    await loadUsers();
  };

  const createUser = async () => {
    await api.post("/api/admin/users", {
      email: userDialog.email,
      name: userDialog.name,
      password: userDialog.password,
      isAdmin: userDialog.isAdmin,
    });
    setUserDialog({ open: false, email: "", name: "", password: "", isAdmin: false });
    await loadUsers();
  };

  const triggerNotifications = async () => {
    if (!window.confirm("לשלוח התראות מנוי פג תוקף ללקוחות רלוונטיים?")) return;
    try {
      const { data } = await api.post("/api/admin/trigger-notifications");
      setNotificationStats(data.stats);
      alert(`נשלחו ${data.stats.sent7d} התראות (7 ימים) ו-${data.stats.sent1d} התראות (יום 1).`);
    } catch (err) {
      alert("שגיאה בשליחת התראות");
    }
  };

  const impersonate = async (id) => {
    if (!window.confirm("להתחבר כמשתמש? נחליף את הטוקן בלוקאל סטורג׳.")) return;
    const { data } = await api.post(`/api/admin/users/${id}/impersonate`);
    if (data?.token) {
      // Keep a backup so we can restore later manually if needed
      const old = localStorage.getItem("aw_token");
      if (old) localStorage.setItem("aw_admin_token_backup", old);
      localStorage.setItem("aw_token", data.token);
      window.location.href = "/overview";
    }
  };

  const setUserSubscription = async (userId, planCodeOrId, months) => {
    const payload = { months: Number(months || 1) };
    if (String(planCodeOrId).match(/^\d+$/)) payload.planId = Number(planCodeOrId);
    else payload.planCode = String(planCodeOrId);
    await api.post(`/api/admin/users/${userId}/subscription/set`, payload);
    await loadUsers();
    await loadBase();
  };

  const extendUserSubscription = async (userId, planCodeOrId, months) => {
    const payload = { months: Number(months || 1) };
    if (String(planCodeOrId).match(/^\d+$/)) payload.planId = Number(planCodeOrId);
    else payload.planCode = String(planCodeOrId);
    await api.post(`/api/admin/users/${userId}/subscription/extend`, payload);
    await loadUsers();
    await loadBase();
  };

  const cancelUserSubscription = async (userId) => {
    await api.post(`/api/admin/users/${userId}/subscription/cancel`);
    await loadUsers();
    await loadBase();
  };

  const openPlanDialog = (u) => {
    setPlanDialog({
      open: true,
      user: u,
      planId: u?.subscription?.plan_id ? String(u.subscription.plan_id) : "",
      months: 1,
      extend: false,
    });
  };

  const applyPlanDialog = async () => {
    const u = planDialog.user;
    if (!u) return;
    if (!planDialog.planId) return;
    if (planDialog.extend) {
      await extendUserSubscription(u.id, planDialog.planId, planDialog.months);
    } else {
      await setUserSubscription(u.id, planDialog.planId, planDialog.months);
    }
    setPlanDialog({ open: false, user: null, planId: "", months: 1, extend: false });
  };

  const openFeaturesEditor = (p) => {
    const obj = normalizeFeatures(p?.features);
    setFeaturesDraft(obj);
    setFeaturesJson(JSON.stringify(obj, null, 2));
    setFeaturesDialog({ open: true, plan: p });
  };

  const toggleFeature = (k, v) => {
    setFeaturesDraft((prev) => ({ ...(prev || {}), [k]: v }));
    setFeaturesJson((prevJson) => {
      try {
        const parsed = JSON.parse(String(prevJson || "{}"));
        const next = { ...(parsed || {}), [k]: v };
        return JSON.stringify(next, null, 2);
      } catch {
        const next = { ...(featuresDraft || {}), [k]: v };
        return JSON.stringify(next, null, 2);
      }
    });
  };

  const saveFeaturesEditor = async () => {
    const p = featuresDialog.plan;
    if (!p) return;
    let obj = featuresDraft;
    try {
      const parsed = JSON.parse(String(featuresJson || "{}"));
      if (parsed && typeof parsed === "object") obj = parsed;
    } catch {
      // keep draft
    }
    await api.put(`/api/admin/plans/${p.id}`, { features: obj });
    await loadBase();
    setFeaturesDialog({ open: false, plan: null });
  };
  const createPlan = async () => {
    setError("");
    if (!planCode || !planName) return setError("חסר code / name");
    await api.post("/api/admin/plans", {
      code: planCode,
      name: planName,
      price: Number(planPrice || 0),
      interval: planInterval,
      active: Number(planActive) ? 1 : 0,
      limits_sites: Number(planLimitsSites || 1),
    });
    setPlanCode("");
    setPlanName("");
    await loadBase();
  };

  const filteredAudit = useMemo(() => {
    const q = String(auditSearch || "").trim().toLowerCase();
    if (!q) return audit;
    return audit.filter((l) => {
      const s = `${l.admin_email || ""} ${l.action || ""} ${l.target_type || ""} ${l.target_id || ""} ${l.meta || ""}`.toLowerCase();
      return s.includes(q);
    });
  }, [audit, auditSearch]);

  const usersPages = useMemo(() => Math.max(1, Math.ceil(usersTotal / usersLimit)), [usersTotal, usersLimit]);

  const exportUsersCsv = async () => {
    const { data } = await api.get("/api/admin/users/export.csv", {
      params: {
        q: userSearch || undefined,
        status: usersStatus || undefined,
        planId: usersPlanId || undefined,
      },
      responseType: "blob",
    });
    const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader title="אדמין (ניהול)" subtitle="משתמשים, חבילות, תמיכה ותפעול המערכת" />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        variant="scrollable"
        scrollButtons={false}
        aria-label="אזורי ניהול"
        sx={{ mb: 2.5, display: "inline-flex", maxWidth: "100%" }}
      >
        <Tab value="users" label="משתמשים" />
        <Tab value="plans" label="חבילות" />
        <Tab value="support" label="תמיכה" />
        <Tab value="audit" label="Audit Logs" />
        <Tab value="ops" label="תפעול" />
      </Tabs>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>

          {tab === "users" ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setUserDialog({ ...userDialog, open: true })}
                >
                  + הוסף משתמש חדש
                </Button>
                <TextField
                  size="small"
                  label="חיפוש (אימייל/שם)"
                  value={userSearch}
                  onChange={(e) => {
                    setUserSearch(e.target.value);
                    setUsersPage(0);
                  }}
                  sx={{ minWidth: 260 }}
                />
                <TextField
                  select
                  size="small"
                  label="סטטוס"
                  value={usersStatus}
                  onChange={(e) => {
                    setUsersStatus(e.target.value);
                    setUsersPage(0);
                  }}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">הכל</MenuItem>
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="disabled">disabled</MenuItem>
                </TextField>
                <TextField
                  select
                  size="small"
                  label="חבילה (מנוי פעיל)"
                  value={usersPlanId}
                  onChange={(e) => {
                    setUsersPlanId(e.target.value);
                    setUsersPage(0);
                  }}
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem value="">הכל</MenuItem>
                  {plans.filter((p) => Number(p.active) === 1).map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {p.code || p.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="לעמוד"
                  value={usersLimit}
                  onChange={(e) => {
                    setUsersLimit(Number(e.target.value));
                    setUsersPage(0);
                  }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={exportUsersCsv}>
                  Export CSV
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  סה״כ: <b>{usersTotal}</b> · עמוד {usersPage + 1}/{usersPages}
                </Typography>
                <Button variant="text" disabled={usersPage <= 0} onClick={() => setUsersPage((p) => Math.max(0, p - 1))}>
                  הקודם
                </Button>
                <Button
                  variant="text"
                  disabled={usersPage >= usersPages - 1}
                  onClick={() => setUsersPage((p) => Math.min(usersPages - 1, p + 1))}
                >
                  הבא
                </Button>
              </Box>
              <Divider />
              {users.map((u) => {
                const sub = u.subscription;
                return (
                  <Box key={u.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 2, display: "grid", gap: 1, "&:hover": { borderColor: (t) => t.aw.borderStrong } }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        #{u.id} · <b>{u.email}</b> {u.name ? `· ${u.name}` : ""} · status: <b>{u.status || "active"}</b>
                      </Typography>
                      <FormControlLabel
                        control={<Switch checked={!!u.is_admin} onChange={() => toggleUserAdmin(u.id, !!u.is_admin)} size="small" />}
                        label="אדמין"
                        sx={{ mr: 2 }}
                      />
                      <TextField
                        select
                        size="small"
                        value={u.status || "active"}
                        onChange={(e) => setUserStatus(u.id, e.target.value)}
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="active">active</MenuItem>
                        <MenuItem value="disabled">disabled</MenuItem>
                      </TextField>
                      <Button variant="outlined" onClick={() => impersonate(u.id)}>
                        התחבר כמשתמש
                      </Button>
                  <Button variant="contained" onClick={() => openPlanDialog(u)}>
                    שינוי חבילה
                  </Button>
                      <Button color="error" variant="text" onClick={() => deleteUser(u.id)}>
                        מחיקה
                      </Button>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        מנוי:{" "}
                        {sub
                          ? `${sub.plan_name || sub.plan_code || "—"} · ${sub.status} · ends: ${sub.ends_at ? String(sub.ends_at) : "—"}`
                          : "—"}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button variant="text" onClick={() => cancelUserSubscription(u.id)}>
                        בטל מנוי פעיל
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {tab === "plans" ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography variant="h6" component="h2">יצירת חבילה</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <TextField size="small" label="code" value={planCode} onChange={(e) => setPlanCode(e.target.value)} />
                <TextField size="small" label="name" value={planName} onChange={(e) => setPlanName(e.target.value)} />
                <TextField size="small" label="price" type="number" value={planPrice} onChange={(e) => setPlanPrice(e.target.value)} />
                <TextField
                  select
                  size="small"
                  label="interval"
                  value={planInterval}
                  onChange={(e) => setPlanInterval(e.target.value)}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="monthly">monthly</MenuItem>
                  <MenuItem value="yearly">yearly</MenuItem>
                </TextField>
                <TextField size="small" label="limits_sites" type="number" value={planLimitsSites} onChange={(e) => setPlanLimitsSites(e.target.value)} />
                <TextField select size="small" label="active" value={planActive} onChange={(e) => setPlanActive(e.target.value)} sx={{ minWidth: 120 }}>
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={0}>0</MenuItem>
                </TextField>
                <Button variant="contained" onClick={createPlan}>
                  צור
                </Button>
              </Box>
              <Divider />
              <Typography variant="h6" component="h2">חבילות קיימות</Typography>
              {plans.slice(0, 100).map((p) => (
                <Box key={p.id} sx={{ display: "grid", gap: 1.5, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                  <Typography variant="body2">
                    #{p.id} · <b>{p.code || p.name}</b> · {p.interval} · ₪{p.price}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <TextField
                      size="small"
                      label="price"
                      defaultValue={p.price}
                      onBlur={(e) => api.put(`/api/admin/plans/${p.id}`, { price: Number(e.target.value) }).then(loadBase)}
                    />
                    <TextField
                      size="small"
                      label="limits_sites"
                      defaultValue={p.limits_sites}
                      onBlur={(e) => api.put(`/api/admin/plans/${p.id}`, { limits_sites: Number(e.target.value) }).then(loadBase)}
                    />
                    <TextField
                      size="small"
                      label="pageviews_included_monthly"
                      defaultValue={p.pageviews_included_monthly}
                      onBlur={(e) =>
                        api.put(`/api/admin/plans/${p.id}`, { pageviews_included_monthly: Number(e.target.value) }).then(loadBase)
                      }
                    />
                    <TextField
                      size="small"
                      label="ai_credits_monthly"
                      defaultValue={p.ai_credits_monthly}
                      onBlur={(e) =>
                        api.put(`/api/admin/plans/${p.id}`, { ai_credits_monthly: Number(e.target.value) }).then(loadBase)
                      }
                    />
                    <TextField
                      size="small"
                      label="active (0/1)"
                      defaultValue={p.active}
                      onBlur={(e) => api.put(`/api/admin/plans/${p.id}`, { active: Number(e.target.value) ? 1 : 0 }).then(loadBase)}
                    />
                    <Button variant="outlined" onClick={() => openFeaturesEditor(p)}>
                      עריכת פיצ׳רים
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : null}

          {tab === "audit" ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography variant="h6" component="h2">Audit Logs</Typography>
              {auditError ? <Alert severity="error">{auditError}</Alert> : null}
              <TextField
                size="small"
                label="חיפוש (admin/action/target/meta)"
                value={auditSearch}
                onChange={(e) => setAuditSearch(e.target.value)}
              />
              <Divider />
              {filteredAudit.slice(0, 200).map((l) => (
                <Box key={l.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, display: "grid", gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    #{l.id} · {l.admin_email} · {l.action}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    target: {l.target_type || "—"} {l.target_id ? `#${l.target_id}` : ""} · {new Date(l.created_at).toLocaleString("he-IL")}
                  </Typography>
                  {l.meta ? (
                    <Typography variant="caption" sx={{ opacity: 0.8, whiteSpace: "pre-wrap" }}>
                      {String(l.meta)}
                    </Typography>
                  ) : null}
                </Box>
              ))}
              <Button variant="outlined" onClick={() => setTab("audit")}>
                רענון
              </Button>
            </Box>
          ) : null}

          {tab === "support" ? (
            <Box sx={{ display: "grid", gap: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" component="h2">פניות מהאתר ({supportTotal})</Typography>
                <Button variant="outlined" onClick={() => setCannedDialog({ open: true, response: null, title: "", content: "" })}>
                  ניהול תבניות
                </Button>
              </Box>
              
              <Box sx={{ display: "grid", gap: 2 }}>
                {supportInquiries.map((inq) => (
                  <Box key={inq.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 2, bgcolor: inq.status === "open" ? (t) => t.aw.softer : "transparent" }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {inq.name} ({inq.email})
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          {new Date(inq.created_at).toLocaleString("he-IL")} · {inq.phone || "אין טלפון"} · {inq.website || "אין אתר"}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Pill tone={inq.status === "open" ? "error" : inq.status === "replied" ? "success" : "neutral"}>
                          {inq.status.toUpperCase()}
                        </Pill>
                        <TextField
                          select
                          size="small"
                          value={inq.status}
                          onChange={(e) => updateInquiry(inq.id, { status: e.target.value, admin_notes: inq.admin_notes })}
                          sx={{ width: 120 }}
                        >
                          <MenuItem value="open">Open</MenuItem>
                          <MenuItem value="replied">Replied</MenuItem>
                          <MenuItem value="closed">Closed</MenuItem>
                        </TextField>
                      </Stack>
                    </Stack>
                    
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: "background.paper", borderRadius: 1.5, border: 1, borderStyle: "dashed", borderColor: "divider" }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{inq.message}</Typography>
                    </Box>

                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Button variant="contained" size="small" onClick={() => setReplyDialog({ open: true, inquiry: inq, message: "", subject: `Accss Support: Re: ${inq.name}` })}>
                        השב במייל
                      </Button>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="הערות אדמין (לא נשלח למשתמש)..."
                        defaultValue={inq.admin_notes}
                        onBlur={(e) => updateInquiry(inq.id, { status: inq.status, admin_notes: e.target.value })}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider />
              <Typography variant="h6" component="h2">תבניות מענה מהירות</Typography>
              <Grid container spacing={2}>
                {cannedResponses.map((res) => (
                  <Grid item xs={12} md={4} key={res.id}>
                    <Card variant="outlined">
                      <CardContent sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{res.title}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', height: 40, overflow: 'hidden', opacity: 0.7 }}>
                          {res.content}
                        </Typography>
                        <Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
                          <Button size="small" onClick={() => setCannedDialog({ open: true, response: res, title: res.title, content: res.content })}>ערוך</Button>
                          <Button size="small" color="error" onClick={() => deleteCanned(res.id)}>מחק</Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}

          {tab === "ops" ? (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography variant="h6" component="h2">פעולות מערכת</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="contained" color="warning" onClick={triggerNotifications}>
                  שלח התראות מנוי פג תוקף (Email)
                </Button>
              </Box>
              <Divider sx={{ my: 1 }} />

              <Typography variant="h6" component="h2">Add-ons</Typography>
              <Divider />
              {addons.slice(0, 50).map((a) => (
                <Box key={a.id} sx={{ display: "grid", gap: 1, mb: 2, p: 1, border: 1, borderColor: "divider", borderRadius: 1.5 }}>
                  <Typography variant="body2">
                    #{a.id} · <b>{a.code}</b>
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <TextField
                      size="small"
                      label="price"
                      defaultValue={a.price}
                      onBlur={(e) => api.put(`/api/admin/addons/${a.id}`, { price: Number(e.target.value) }).then(loadBase)}
                    />
                    <TextField
                      size="small"
                      label="add_sites"
                      defaultValue={a.add_sites}
                      onBlur={(e) => api.put(`/api/admin/addons/${a.id}`, { add_sites: Number(e.target.value) }).then(loadBase)}
                    />
                    <TextField
                      size="small"
                      label="add_pageviews_monthly"
                      defaultValue={a.add_pageviews_monthly}
                      onBlur={(e) =>
                        api.put(`/api/admin/addons/${a.id}`, { add_pageviews_monthly: Number(e.target.value) }).then(loadBase)
                      }
                    />
                    <TextField
                      size="small"
                      label="add_ai_credits_monthly"
                      defaultValue={a.add_ai_credits_monthly}
                      onBlur={(e) =>
                        api.put(`/api/admin/addons/${a.id}`, { add_ai_credits_monthly: Number(e.target.value) }).then(loadBase)
                      }
                    />
                    <TextField
                      size="small"
                      label="active (0/1)"
                      defaultValue={a.active}
                      onBlur={(e) => api.put(`/api/admin/addons/${a.id}`, { active: Number(e.target.value) ? 1 : 0 }).then(loadBase)}
                    />
                  </Box>
                </Box>
              ))}

              <Typography variant="h6" component="h2">אתרים</Typography>
              <Divider />
              {sites.slice(0, 50).map((s) => (
                <Box key={s.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    #{s.id} · {s.email} · {s.name} · {s.domain} · <b>{s.status}</b>
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={s.status}
                    onChange={(e) => setSiteStatus(s.id, e.target.value)}
                  >
                    <MenuItem value="active">active</MenuItem>
                    <MenuItem value="disabled">disabled</MenuItem>
                  </TextField>
                </Box>
              ))}

              <Typography variant="h6" component="h2">Subscriptions</Typography>
              <Divider />
              {subscriptions.slice(0, 50).map((s) => (
                <Box key={s.id} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    #{s.id} · {s.email} · {s.plan_name} · <b>{s.status}</b> · ends: {String(s.ends_at)}
                  </Typography>
                  <TextField
                    select
                    size="small"
                    value={s.status}
                    onChange={(e) => setSubStatus(s.id, e.target.value)}
                  >
                    <MenuItem value="active">active</MenuItem>
                    <MenuItem value="cancelled">cancelled</MenuItem>
                    <MenuItem value="expired">expired</MenuItem>
                  </TextField>
                </Box>
              ))}

              <Typography variant="h6" component="h2">Transactions</Typography>
              <Divider />
              {transactions.slice(0, 50).map((t) => (
                <Typography key={t.id} variant="body2">
                  #{t.id} · {t.email} · {t.plan_name} · ₪{formatShekelsFromAgorot(t.amount)} ·{" "}
                  <b>{t.status}</b> · {t.provider_transaction_id}
                </Typography>
              ))}

              <Button sx={{ mt: 1 }} variant="outlined" onClick={() => loadBase()}>
                רענון
              </Button>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      <Dialog
        open={planDialog.open}
        onClose={() => setPlanDialog({ open: false, user: null, planId: "", months: 1 })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>שינוי חבילה למשתמש</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            משתמש: <b>{planDialog.user?.email}</b>
          </Typography>
          <TextField
            select
            label="בחר חבילה"
            value={planDialog.planId}
            onChange={(e) => setPlanDialog((p) => ({ ...p, planId: e.target.value }))}
          >
            {plans
              .filter((p) => Number(p.active) === 1)
              .map((p) => (
                <MenuItem key={p.id} value={String(p.id)}>
                  {p.code || p.name} · {p.interval} · ₪{p.price}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            type="number"
            label="חודשים"
            value={planDialog.months}
            onChange={(e) => setPlanDialog((p) => ({ ...p, months: Number(e.target.value || 1) }))}
            inputProps={{ min: 1, max: 36 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={planDialog.extend}
                onChange={(e) => setPlanDialog((p) => ({ ...p, extend: e.target.checked }))}
              />
            }
            label="הארך מנוי קיים (במקום ליצור חדש מהיום)"
          />
          <Alert severity="info">
            {planDialog.extend
              ? "הארכה תוסיף חודשים לתאריך הסיום הקיים של המשתמש."
              : "הפעולה יוצרת מנוי פעיל חדש שמתחיל היום ומסיימת מנוי פעיל קודם (אם קיים)."}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ open: false, user: null, planId: "", months: 1 })}>סגור</Button>
          <Button
            color="error"
            variant="text"
            onClick={() => planDialog.user?.id && cancelUserSubscription(planDialog.user.id)}
            disabled={!planDialog.user?.id}
          >
            בטל מנוי פעיל
          </Button>
          <Button variant="contained" onClick={applyPlanDialog} disabled={!planDialog.user || !planDialog.planId}>
            עדכן חבילה
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={featuresDialog.open} onClose={() => setFeaturesDialog({ open: false, plan: null })} maxWidth="md" fullWidth>
        <DialogTitle>עריכת פיצ׳רים – {featuresDialog.plan?.code || featuresDialog.plan?.name}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            עריכה מהירה באמצעות toggles + אפשרות לערוך JSON מלא.
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            {knownFeatureKeys.map((k) => (
              <FormControlLabel
                key={k}
                control={<Switch checked={featuresDraft?.[k] === true} onChange={(e) => toggleFeature(k, e.target.checked)} />}
                label={k}
              />
            ))}
          </Box>
          <TextField
            label="features (JSON)"
            value={featuresJson}
            onChange={(e) => setFeaturesJson(e.target.value)}
            multiline
            minRows={10}
          />
          <Alert severity="warning">
            שינוי פיצ׳רים משפיע על הווידג׳ט והגייטינג. מומלץ לשנות בזהירות ולבדוק באתר דמו.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeaturesDialog({ open: false, plan: null })}>סגור</Button>
          <Button variant="contained" onClick={saveFeaturesEditor} disabled={!featuresDialog.plan}>
            שמור פיצ׳רים
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialog.open} onClose={() => setReplyDialog({ open: false, inquiry: null, message: "", subject: "" })} maxWidth="md" fullWidth>
        <DialogTitle>השבה לפנייה: {replyDialog.inquiry?.name}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>בחר תבנית מהירה:</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {cannedResponses.map(res => (
                <Button key={res.id} variant="outlined" size="small" onClick={() => applyCannedResponse(res)}>
                  {res.title}
                </Button>
              ))}
            </Stack>
          </Box>
          <TextField
            label="נושא המייל"
            fullWidth
            value={replyDialog.subject}
            onChange={(e) => setReplyDialog(prev => ({ ...prev, subject: e.target.value }))}
          />
          <TextField
            label="תוכן המייל (HTML/Plain Text)"
            fullWidth
            multiline
            minRows={10}
            value={replyDialog.message}
            onChange={(e) => setReplyDialog(prev => ({ ...prev, message: e.target.value }))}
          />
          <Typography variant="caption" sx={{ opacity: 0.7 }}>המייל יישלח אל: {replyDialog.inquiry?.email}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialog({ open: false, inquiry: null, message: "", subject: "" })}>ביטול</Button>
          <Button variant="contained" onClick={sendReply} disabled={!replyDialog.message}>שלח מענה</Button>
        </DialogActions>
      </Dialog>

      {/* Canned Response Editor */}
      <Dialog open={cannedDialog.open} onClose={() => setCannedDialog({ open: false, response: null, title: "", content: "" })} maxWidth="sm" fullWidth>
        <DialogTitle>{cannedDialog.response ? 'עריכת תבנית' : 'תבנית חדשה'}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <TextField
            label="כותרת התבנית"
            fullWidth
            value={cannedDialog.title}
            onChange={(e) => setCannedDialog(prev => ({ ...prev, title: e.target.value }))}
          />
          <TextField
            label="תוכן התבנית"
            fullWidth
            multiline
            minRows={8}
            value={cannedDialog.content}
            onChange={(e) => setCannedDialog(prev => ({ ...prev, content: e.target.value }))}
            helperText="ניתן להשתמש ב-[NAME] כדי להוסיף את שם הפונה באופן אוטומטי."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCannedDialog({ open: false, response: null, title: "", content: "" })}>ביטול</Button>
          <Button variant="contained" onClick={saveCanned}>שמור תבנית</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={userDialog.open} onClose={() => setUserDialog({ ...userDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>הוספת משתמש חדש</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 2 }}>
          <TextField
            label="אימייל"
            fullWidth
            value={userDialog.email}
            onChange={(e) => setUserDialog({ ...userDialog, email: e.target.value })}
          />
          <TextField
            label="שם"
            fullWidth
            value={userDialog.name}
            onChange={(e) => setUserDialog({ ...userDialog, name: e.target.value })}
          />
          <TextField
            label="סיסמה"
            type="password"
            fullWidth
            value={userDialog.password}
            onChange={(e) => setUserDialog({ ...userDialog, password: e.target.value })}
          />
          <FormControlLabel
            control={<Switch checked={userDialog.isAdmin} onChange={(e) => setUserDialog({ ...userDialog, isAdmin: e.target.checked })} />}
            label="משתמש אדמין"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog({ ...userDialog, open: false })}>ביטול</Button>
          <Button variant="contained" onClick={createUser} disabled={!userDialog.email || !userDialog.password}>
            צור משתמש
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}


